/**
 * URL 参数加解密工具
 *
 * 用于对 URL 中的敏感 ID（如 pid、id、productID）进行 XOR + Base64 混淆，
 * 防止用户直接通过修改 URL 参数访问未授权的数据。
 *
 * 用法：
 *   import { encodeParam, decodeParam } from '@/utils/urlCrypto'
 *
 *   // 构建 URL 时加密
 *   var safe = encodeParam('123')          // → "HhM%3D"
 *   router.push({ query: { pid: safe } }) // → /page?pid=HhM%3D
 *
 *   // 读取 URL 时解密
 *   var rawId = decodeParam(this.$route.query.pid)  // → "123"
 */

var KEY = 'KAMS'

/**
 * 将明文字符串加密为 URL-safe 字符串
 * @param {string|number} value 原始值
 * @returns {string} 加密后的字符串
 */
export function encodeParam(value) {
  if (value === null || value === undefined) return ''
  var raw = String(value)
  var bytes = []
  for (var i = 0; i < raw.length; i++) {
    bytes.push(raw.charCodeAt(i) ^ KEY.charCodeAt(i % KEY.length))
  }
  var binary = ''
  for (var j = 0; j < bytes.length; j++) {
    binary += String.fromCharCode(bytes[j])
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * 将加密字符串解密为明文
 * @param {string} encoded 加密字符串
 * @returns {string} 解密后的原始值，解密失败返回原值
 */
export function decodeParam(encoded) {
  if (!encoded || typeof encoded !== 'string') return encoded
  // 兼容加密功能上线前生成的纯数字 ID 链接。纯数字不是本工具
  // encodeParam 的合法输出，不能继续交给 atob，否则可能被误解成乱码。
  if (/^\d+$/.test(encoded)) return encoded
  try {
    var b64 = encoded.replace(/-/g, '+').replace(/_/g, '/')
    while (b64.length % 4) { b64 += '=' }
    var binary = atob(b64)
    var raw = ''
    for (var i = 0; i < binary.length; i++) {
      raw += String.fromCharCode(binary.charCodeAt(i) ^ KEY.charCodeAt(i % KEY.length))
    }
    return raw
  } catch (e) {
    // 解密失败：可能是旧版明文链接，直接返回原值
    return encoded
  }
}

/**
 * 加密多个值，返回逗号分隔的加密字符串
 * 用于分享选品等批量场景
 * @param {Array} values
 * @returns {string}
 */
export function encodeParamList(values) {
  if (!values || !values.length) return ''
  return values.map(function(v) { return encodeParam(v) }).join(',')
}

/**
 * 解密逗号分隔的加密字符串，返回明文数组
 * @param {string} encoded
 * @returns {string[]}
 */
export function decodeParamList(encoded) {
  if (!encoded) return []
  return encoded.split(',').map(function(s) {
    return decodeParam(s.trim())
  }).filter(Boolean)
}
